<template>
    <Head title="Team results" />
    <div class="flex flex-col h-full overflow-y-auto bg-valence-grey-50">
        <div class="max-w-[860px] w-full mx-auto px-10 py-10 flex flex-col gap-6">
            <header>
                <div class="flex items-center gap-2 mb-2">
                    <a href="/reflections" class="body-small text-valence-grey-700 hover:text-valence-grey-850 flex items-center gap-1">
                        <i class="bi bi-chevron-left" aria-hidden="true"></i> Reflections
                    </a>
                </div>
                <BasePageTitle title="Team results" />
                <p class="mt-2 body text-valence-grey-750">See your team members' assessment progress and released reports.</p>
            </header>

            <div v-if="loading" class="body-small text-valence-grey-750">Loading…</div>
            <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

            <!-- Empty state: no team yet -->
            <BaseCard v-else-if="teams.length === 0" padding="lg" class="flex flex-col items-center text-center gap-3 py-12">
                <i class="bi bi-people text-4xl text-valence-grey-700" aria-hidden="true"></i>
                <BaseHeader size="s" class="text-valence-grey-850">No teams led by you yet</BaseHeader>
                <p class="body-small text-valence-grey-750 max-w-md">
                    Teams are set up by your organization's assessment admin. Once you're designated as a team's lead, your members' assessment results appear here.
                </p>
            </BaseCard>

            <template v-else>
                <section v-for="team in teams" :key="team.team_id" class="flex flex-col gap-3">
                    <h2 class="body-small-bold uppercase tracking-wide text-valence-grey-750">{{ team.team_name || "Your team" }}</h2>
                    <div v-if="team.members.length === 0" class="rounded-xl border border-dashed border-valence-grey-200 bg-white px-5 py-6 body-small text-valence-grey-750">
                        No members yet. Your assessment admin can add them.
                    </div>
                    <BaseCard v-for="member in team.members" :key="member.user_uuid" padding="md" class="flex flex-col gap-3">
                        <div class="flex items-center justify-between gap-4">
                            <div class="min-w-0">
                                <div class="body-small-bold text-valence-grey-850 truncate">{{ member.name || member.email }}</div>
                                <div v-if="member.name" class="details text-valence-grey-600 truncate">{{ member.email }}</div>
                            </div>
                        </div>
                        <div v-if="member.cycles.length === 0" class="body-small text-valence-grey-600">No assessments yet.</div>
                        <div v-else class="flex flex-col gap-2">
                            <div v-for="cycle in member.cycles" :key="cycle.cycle_id" class="flex items-center justify-between gap-3 flex-wrap">
                                <div class="flex items-center gap-2 min-w-0">
                                    <span class="body-small text-valence-grey-850 truncate">{{ cycle.campaign_name || cycle.template_name }}</span>
                                    <StatusChip :state="cycleStateChipState(cycle.state)">{{ cycleStateLabel(cycle.state) }}</StatusChip>
                                </div>
                                <a
                                    v-if="cycle.report_available"
                                    :href="`/reflections/360/${cycle.cycle_id}/results`"
                                    class="body-small text-valence-blue-600 hover:text-valence-blue-700 inline-flex items-center gap-1 shrink-0">
                                    View report
                                    <i class="bi bi-arrow-right" aria-hidden="true"></i>
                                </a>
                                <span v-else class="details text-valence-grey-600 shrink-0">No report yet</span>
                            </div>
                        </div>
                    </BaseCard>
                </section>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { TeamResults, TeamResultsResponse } from "~vue/api/generated/reflections";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseHeader from "~vue/components/BaseHeader.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { cycleStateChipState, cycleStateLabel } from "~vue/utils/threeSixty";
import { onMounted, ref } from "vue";

const teams = ref<TeamResults[]>([]);
const loading = ref(true);
const loadError = ref<string | null>(null);

async function reload() {
    const { data, error: fetchError } = await useFetch("/v1/reflections/team-results").json<TeamResultsResponse>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "Could not load team results.";
        return;
    }
    loadError.value = null;
    teams.value = data.value?.teams ?? [];
}

onMounted(reload);
</script>
